import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, X, Star, Rocket, Wallet, Link2, Target, Globe, Shield, ShieldCheck, BadgePercent, Sparkles, ChevronDown, Building2 } from 'lucide-react';
import { PLANS, PRICING_FEATURES, WHY_CHOOSE_ITEMS, USD_PRICING, getAnnualTotal } from '../utils/pricing';
import { useAuth } from '../contexts/AuthContext';
import { PublicHeader } from '../components/PublicHeader';
import Footer from '../components/Footer';

const iconMap = { rocket: Rocket, wallet: Wallet, link: Link2, target: Target, globe: Globe, shield: Shield };

const colorClasses = {
  indigo:  { bg: 'bg-indigo-100',  text: 'text-indigo-600',  border: 'border-indigo-200' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
  violet:  { bg: 'bg-violet-100',  text: 'text-violet-600',  border: 'border-violet-200' },
  amber:   { bg: 'bg-amber-100',   text: 'text-amber-600',   border: 'border-amber-200' },
  rose:    { bg: 'bg-rose-100',    text: 'text-rose-600',    border: 'border-rose-200' },
  cyan:    { bg: 'bg-cyan-100',    text: 'text-cyan-600',    border: 'border-cyan-200' },
};

const SALES_EMAIL = 'sales@formlio.app';

export const PricingPage = () => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [hoveredRow, setHoveredRow] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const getPrice = (planKey) => {
    if (planKey === 'FREE') return { display: 'Free' };
    if (planKey === 'ENTERPRISE') return { display: 'Custom' };
    const planPricing = planKey === 'GROWTH' ? USD_PRICING.growth : USD_PRICING.pro;
    if (billingPeriod === 'annual') {
      return { display: `$${planPricing.annual}`, total: getAnnualTotal(planPricing.annual) };
    }
    return { display: `$${planPricing.monthly}` };
  };

  const handlePlanSelect = (planKey) => {
    if (planKey === 'ENTERPRISE') {
      const mailto = `mailto:${SALES_EMAIL}?subject=Formlio%20Enterprise%20Inquiry&body=Hi%20Formlio%20team%2C%0A%0AI%27d%20like%20to%20discuss%20an%20Enterprise%20plan%20for%20our%20team.%0A%0ATeam%20size%3A%0AExpected%20monthly%20submissions%3A%0AKey%20requirements%3A%0A%0AThanks%21`;
      window.location.assign(mailto);
      return;
    }
    if (!isAuthenticated) {
      navigate('/signup');
    } else if (planKey !== 'FREE') {
      navigate('/billing');
    }
  };

  const isCurrentPlan = (planKey) => {
    if (!user) return false;
    return (user.plan || 'free').toLowerCase() === planKey.toLowerCase();
  };

  const renderFeatureRow = (feature, value) => {
    const isBoolean = typeof value === 'boolean';

    if (feature.label === 'Formlio branding displayed') {
      return (
        <div className="flex items-center gap-2">
          {value ? <X className="w-4 h-4 text-red-400 shrink-0" /> : <Check className="w-4 h-4 text-green-500 shrink-0" />}
          <span className={`text-sm ${!value ? 'text-slate-700' : 'text-slate-400'}`}>
            {value ? 'Formlio branding displayed' : 'Option to remove Formlio branding'}
          </span>
        </div>
      );
    }

    if (isBoolean) {
      return (
        <div className="flex items-center gap-2">
          {value ? <Check className="w-4 h-4 text-green-500 shrink-0" /> : <X className="w-4 h-4 text-red-400 shrink-0" />}
          <span className={`text-sm ${value ? 'text-slate-700' : 'text-slate-400'}`}>{feature.label}</span>
        </div>
      );
    }

    return (
      <div className="flex items-start gap-2">
        <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
        <span className="text-sm leading-tight">
          <span className="font-bold text-slate-800">{value}</span>
          <span className="text-slate-500 ml-1">{feature.label}</span>
        </span>
      </div>
    );
  };

  // Card config drives the 4-column grid
  const cards = [
    {
      key: 'FREE',
      plan: PLANS.FREE,
      featureKey: 'free',
      emoji: '🟢',
      borderColor: 'border-emerald-300',
      hoverBg: 'bg-emerald-50',
      btnClass: 'bg-emerald-500 hover:bg-emerald-600 text-white',
      includesLabel: 'Includes:',
      priceSuffix: '',
      popular: false,
    },
    {
      key: 'GROWTH',
      plan: PLANS.GROWTH,
      featureKey: 'growth',
      emoji: '🔵',
      borderColor: 'border-indigo-200',
      hoverBg: 'bg-indigo-50',
      btnClass: 'bg-indigo-600 hover:bg-indigo-700 text-white',
      includesLabel: `Everything in ${PLANS.FREE.name}, plus:`,
      priceSuffix: '/mo',
      popular: false,
    },
    {
      key: 'PRO',
      plan: PLANS.PRO,
      featureKey: 'pro',
      emoji: '🟠',
      borderColor: 'border-amber-400',
      hoverBg: 'bg-amber-50',
      btnClass: 'bg-amber-500 hover:bg-amber-600 text-white',
      includesLabel: `Everything in ${PLANS.GROWTH.name}, plus:`,
      priceSuffix: '/mo',
      popular: true,
    },
    {
      key: 'ENTERPRISE',
      plan: PLANS.ENTERPRISE,
      featureKey: 'enterprise',
      emoji: '🟣',
      borderColor: 'border-violet-300',
      hoverBg: 'bg-violet-50',
      btnClass: 'bg-slate-900 hover:bg-slate-800 text-white',
      includesLabel: `Everything in ${PLANS.PRO.name}, plus:`,
      priceSuffix: '',
      popular: false,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-br from-slate-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <Badge className="mb-3 bg-indigo-100 text-indigo-700 px-3 py-1 text-xs">Trusted by 1000+ users</Badge>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-3" data-testid="pricing-hero-heading">
              Simple Pricing for Teams of{' '}
              <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">Every Size</span>
            </h1>
            <p className="text-base text-slate-600">Start free. Upgrade when you&apos;re ready. One flat USD plan worldwide.</p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-2 mt-6" data-testid="pricing-billing-toggle">
              <button
                onClick={() => setBillingPeriod('monthly')}
                data-testid="billing-monthly-btn"
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billingPeriod === 'monthly' ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                data-testid="billing-yearly-btn"
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${billingPeriod === 'annual' ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                Yearly <Badge className="bg-green-500 text-white text-xs px-2">save 20%</Badge>
              </button>
            </div>
          </div>

          {/* Pricing Cards — 4-column layout */}
          <div className="max-w-7xl mx-auto px-2 sm:px-0">
            {/* Most Popular Badge row (desktop only) — sits above Business (3rd) card */}
            <div className="hidden lg:grid grid-cols-4 gap-4">
              <div></div>
              <div></div>
              <div className="flex justify-center -mb-2 relative z-10">
                <div className="bg-amber-500 text-white px-4 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 shadow-md">
                  <Star className="w-3 h-3 fill-current" /> Most Popular
                </div>
              </div>
              <div></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {cards.map((card) => {
                const price = getPrice(card.key);
                const current = isCurrentPlan(card.key);
                return (
                  <div
                    key={card.key}
                    className={`bg-white rounded-xl border-2 ${card.borderColor} ${card.popular ? 'shadow-lg' : 'shadow-sm'} overflow-hidden flex flex-col`}
                    data-testid={`pricing-card-${card.plan.id}`}
                  >
                    {/* Most Popular Badge — mobile/tablet */}
                    {card.popular && (
                      <div className="lg:hidden flex justify-center -mb-3 pt-2">
                        <div className="bg-amber-500 text-white px-4 py-1.5 rounded-full text-xs font-medium flex items-center gap-1">
                          <Star className="w-3 h-3 fill-current" /> Most Popular
                        </div>
                      </div>
                    )}

                    {/* Header */}
                    <div className={`p-4 text-center border-b ${card.borderColor.replace('border-', 'border-').replace('-300', '-100').replace('-400', '-100').replace('-200', '-100')}`}>
                      <h3 className="text-xl font-bold text-slate-900">
                        {card.emoji} {card.plan.name}
                      </h3>
                      <p className="text-xs text-slate-500 mt-1 min-h-[2rem]">{card.plan.description}</p>
                      <div className="mt-3">
                        <span className="text-3xl font-bold text-slate-900" data-testid={`price-${card.plan.id}`}>{price.display}</span>
                        {card.priceSuffix && <span className="text-slate-500 text-sm">{card.priceSuffix}</span>}
                      </div>
                      {/* Annual disclosure — keeps "$15/mo · billed annually" unambiguous */}
                      {card.priceSuffix && billingPeriod === 'annual' && (
                        <p className="text-xs text-emerald-600 font-medium mt-1" data-testid={`price-${card.plan.id}-annual-note`}>
                          billed annually
                        </p>
                      )}
                      <Button
                        className={`w-full mt-3 ${card.btnClass} font-semibold py-2 text-sm`}
                        onClick={() => handlePlanSelect(card.key)}
                        disabled={current}
                        data-testid={`cta-${card.plan.id}`}
                      >
                        {current ? 'Current Plan' : card.plan.cta}
                      </Button>
                    </div>

                    {/* Features */}
                    <div className="p-3 flex-1">
                      <p className="text-xs font-semibold text-slate-700 mb-2 px-2">{card.includesLabel}</p>
                      {PRICING_FEATURES.map((feature, idx) => (
                        <div
                          key={idx}
                          className={`py-1.5 px-2 rounded transition-colors ${hoveredRow === idx ? card.hoverBg : ''}`}
                          onMouseEnter={() => setHoveredRow(idx)}
                          onMouseLeave={() => setHoveredRow(null)}
                        >
                          {renderFeatureRow(feature, feature[card.featureKey])}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Trust / Guarantee Strip */}
      <section className="py-8 bg-white border-y border-slate-100" data-testid="pricing-trust-strip">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { Icon: ShieldCheck, title: 'Secure payments',    sub: 'PCI-compliant Razorpay & Stripe' },
              { Icon: BadgePercent, title: 'No hidden fees',     sub: 'What you see is what you pay' },
              { Icon: X,           title: 'Cancel anytime',     sub: 'No contracts, no lock-in' },
              { Icon: Sparkles,    title: '14-day money-back',  sub: 'Risk-free guarantee on paid plans' },
            ].map(({ Icon, title, sub }) => (
              <div key={title} className="flex items-start gap-3" data-testid={`trust-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                <span className="shrink-0 w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center ring-1 ring-emerald-100">
                  <Icon className="w-4 h-4" strokeWidth={2.4} />
                </span>
                <div>
                  <p className="text-sm font-bold text-slate-900 leading-tight">{title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Banner */}
      <section className="py-12 bg-gradient-to-br from-slate-900 via-slate-800 to-violet-900" data-testid="pricing-enterprise-banner">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-violet-500/20 ring-1 ring-violet-400/30 flex items-center justify-center shrink-0">
                <Building2 className="w-6 h-6 text-violet-300" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-1">Need more? Let&apos;s talk Enterprise.</h3>
                <p className="text-sm text-slate-300">Custom quotas, SSO, dedicated infra, SLA-backed support. Built around your team.</p>
              </div>
            </div>
            <Button
              size="lg"
              className="bg-white text-slate-900 hover:bg-slate-100 font-semibold px-6 shrink-0"
              onClick={() => handlePlanSelect('ENTERPRISE')}
              data-testid="enterprise-banner-cta"
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-slate-900 mb-3">Why Choose Formlio?</h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">Everything you need to build powerful forms and grow your business.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE_ITEMS.map((item) => {
              const IconComponent = iconMap[item.icon];
              const colors = colorClasses[item.color] || colorClasses.indigo;
              return (
                <div key={item.id} className={`bg-white p-5 rounded-xl border-2 ${colors.border} hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}>
                  <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center mb-3`}>
                    {IconComponent && <IconComponent className={`w-6 h-6 ${colors.text}`} />}
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50" data-testid="pricing-faq-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <Badge className="mb-3 bg-violet-100 text-violet-700 px-3 py-1 text-xs">FAQ</Badge>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-slate-900 mb-3">
              Questions? We&apos;ve got answers.
            </h2>
            <p className="text-base text-slate-600">
              Still curious? <button onClick={() => navigate('/contact')} className="text-indigo-600 hover:text-indigo-700 font-semibold underline-offset-2 hover:underline">Talk to our team</button>.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { q: 'Can I cancel anytime?', a: 'Yes — every paid plan is month-to-month (or year-to-year if you chose yearly). Cancel from your billing page in one click and you keep access until the end of your billing period. No phone calls, no retention tactics.' },
              { q: 'Is there a free trial?', a: 'Our Starter plan is forever free — no credit card needed. If you want to try Professional or Business, every paid plan includes a 14-day money-back guarantee. Test every feature risk-free.' },
              { q: 'Do you charge per submission?', a: 'No per-submission fees. Each plan includes a generous monthly submission allowance, and you only ever pay your flat plan price. Heavy usage? Move up a tier — no surprise overage invoices.' },
              { q: 'Why USD only?', a: 'One transparent global price. No regional games, no opaque conversions. Your card issuer handles the FX at the real interbank rate. Whether you\'re in Mumbai, Munich, or Manhattan — you pay the same flat USD plan.' },
              { q: 'Is support included in every plan?', a: 'Yes. Starter gets community + email support. Professional includes priority email support (under 24h response). Business adds chat + a dedicated success manager. Enterprise includes SLA-backed support.' },
              { q: 'Can I switch plans later?', a: 'Absolutely. Upgrade or downgrade anytime from your billing page. Upgrades are prorated instantly; downgrades take effect at the end of your current billing cycle so you never lose paid-for days.' },
              { q: 'How secure is my payment data?', a: 'We never touch your card data directly. All payments are processed through PCI-DSS compliant providers (Razorpay & Stripe) using tokenization. Your form submission data is encrypted at rest and in transit (TLS 1.3).' },
            ].map((item, idx) => {
              const open = openFaq === idx;
              return (
                <div key={item.q} className={`bg-white rounded-xl border ${open ? 'border-indigo-300 shadow-md' : 'border-slate-200'} transition-all`} data-testid={`faq-item-${idx}`}>
                  <button
                    onClick={() => setOpenFaq(open ? -1 : idx)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                    aria-expanded={open}
                    data-testid={`faq-toggle-${idx}`}
                  >
                    <span className="text-sm md:text-base font-semibold text-slate-900">{item.q}</span>
                    <ChevronDown className={`w-5 h-5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180 text-indigo-600' : ''}`} />
                  </button>
                  {open && (
                    <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed" data-testid={`faq-answer-${idx}`}>
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-violet-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">Ready to get started?</h2>
          <p className="text-indigo-100 mb-6">Join thousands of businesses using Formlio to capture leads and grow.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold px-6" onClick={() => navigate(isAuthenticated ? '/dashboard' : '/signup')} data-testid="cta-start-free">
              Start Free Today
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-semibold px-6" onClick={() => handlePlanSelect('ENTERPRISE')} data-testid="cta-contact-sales">
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
